import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';
import { useExecutionRecordsApi, ExecutionRecord } from '../apis/execution-records-apis';
import { useUsersApi } from '../apis/users-apis';
import { UserData } from '../types';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/Table";
import { LogViewerModal } from './LogViewerModal';

interface ExecutionRecordsDisplayProps {
  playbookId: string;
  playbookName?: string;
  maxRecords?: number;
  hidePlaybookName?: boolean;
  hideOwner?: boolean;
}

export const ExecutionRecordsDisplay: React.FC<ExecutionRecordsDisplayProps> = ({ 
  playbookId,
  playbookName,
  maxRecords = 10,
  hidePlaybookName = false,
  hideOwner = false
}) => {
  const [records, setRecords] = useState<ExecutionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [userMap, setUserMap] = useState<Map<string, string>>(new Map());
  
  // Log viewer modal state
  const [selectedLogUrl, setSelectedLogUrl] = useState<string | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedLogType, setSelectedLogType] = useState<'ai' | 'execution' | null>(null);
  
  const { getExecutionRecords } = useExecutionRecordsApi();
  const { getUsers } = useUsersApi();

  // Fetch users to map owner IDs to emails
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await getUsers();
        const userMap = new Map();
        usersData.forEach((user: UserData) => {
          userMap.set(user.userId, user.email);
        });
        setUserMap(userMap);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchRecords = async () => {
      if (!playbookId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await getExecutionRecords(playbookId, maxRecords, currentPage);
        setRecords(response.items);
        setTotalRecords(response.metadata.scannedCount);
        setTotalPages(Math.ceil(response.metadata.scannedCount / maxRecords));
      } catch (err) {
        console.error('Error fetching execution records:', err);
        setError('Failed to load execution records. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [playbookId, currentPage, maxRecords]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const getStatusIcon = (result: string) => {
    if (!result) {
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
    
    switch (result.toLowerCase()) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const handleOpenLogModal = (logUrl: string, type: 'ai' | 'execution') => {
    setSelectedLogUrl(logUrl);
    setSelectedLogType(type);
    setIsLogModalOpen(true);
  };

  const handleCloseLogModal = () => {
    setIsLogModalOpen(false);
    setSelectedLogUrl(null);
    setSelectedLogType(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p>{error}</p>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="p-4 bg-gray-50 text-gray-500 rounded-md text-center">
        No execution records found for this playbook.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="w-full">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/7 text-center">Date</TableHead>
              <TableHead className="w-1/7 text-center">Status</TableHead>
              {!hidePlaybookName && <TableHead className="w-1/7 text-center">Playbook Name</TableHead>}
              <TableHead className="w-1/7 text-center">Execution ID</TableHead>
              {!hideOwner && <TableHead className="w-1/7 text-center">Owner</TableHead>}
              <TableHead className="w-1/7 text-center">AI Log</TableHead>
              <TableHead className="w-1/7 text-center">Execution Log</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((record) => (
              <TableRow key={record?.executionId || 'unknown'}>
                <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                  {formatDate(record?.executionDate)}
                </td>
                <td className="px-2 py-2 text-sm truncate text-center">
                  <div className="flex items-center justify-center">
                    {getStatusIcon(record?.result)}
                    <span className="ml-1 truncate">{record?.result || 'Unknown'}</span>
                  </div>
                </td>
                {!hidePlaybookName && (
                  <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                    {playbookName || record?.playbookName || 'N/A'}
                  </td>
                )}
                <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                  {record?.executionId || 'N/A'}
                </td>
                {!hideOwner && (
                  <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                    {record?.ownerId ? userMap.get(record.ownerId) || record.ownerId : 'N/A'}
                  </td>
                )}
                <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                  {record?.aiLog ? (
                    <button 
                      onClick={() => handleOpenLogModal(record.aiLog!, 'ai')}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    >
                      View AI Log
                    </button>
                  ) : 'N/A'}
                </td>
                <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                  {record?.executionLog ? (
                    <button 
                      onClick={() => handleOpenLogModal(record.executionLog!, 'execution')}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    >
                      View Execution Log
                    </button>
                  ) : 'N/A'}
                </td>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-700">
          Showing <span className="font-medium">{((currentPage - 1) * maxRecords) + 1}-{Math.min(currentPage * maxRecords, totalRecords)}</span> of{' '}
          <span className="font-medium">{totalRecords}</span> records
        </p>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Log Viewer Modal */}
      {selectedLogUrl && selectedLogType && (
        <LogViewerModal
          logUrl={selectedLogUrl}
          isOpen={isLogModalOpen}
          onClose={handleCloseLogModal}
          title={selectedLogType === 'ai' ? "AI Log Viewer" : "Execution Log Viewer"}
          type={selectedLogType}
        />
      )}
    </div>
  );
}; 